import React from 'react';

/**
 * One field of a catalog variant's form.
 *
 * Pulled out of Catalog.js so the page can render fields inside grouped sections instead of
 * one flat 22-item column. The input behaviour is unchanged — only where it sits.
 */
const FIELD = {
  padding: '7px 11px', width: '100%', maxWidth: 320, borderRadius: 8, fontSize: 13,
  border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--ink-2)',
};

export default function FormField({ fieldKey: key, spec, form, setForm, nodeRoles, clusterNodes,
                                    nodeTaints = {}, nodesByRole = {}, storageClasses = [] }) {
  const set = (v) => setForm({ ...form, [key]: v });

  // A node pin (manual-PV hostname, or a role matching one or more nodes) bypasses the
  // scheduler's normal candidate filtering the same way a hard taint does. Warn, don't
  // block — some taints are intentional reserved capacity the operator may want anyway.
  const taintsOf = (nodeName) => nodeTaints[nodeName];
  const roleHasTaintedNode = (role) => (nodesByRole[role] || []).some((n) => nodeTaints[n]);

  let input;
  if (key === 'role' || key === 'worker_role') {
    // Placement picks from labels that actually exist on the cluster, so a typo cannot
    // produce a pod that never schedules.
    const selectedTainted = form[key] && roleHasTaintedNode(form[key]);
    input = (
      <>
        <select value={form[key] ?? ''} onChange={(e) => set(e.target.value)} style={FIELD}>
          <option value="">(any node — no pinning)</option>
          {nodeRoles.map((r) => (
            <option key={r} value={r}>{roleHasTaintedNode(r) ? `⚠ ${r}` : r}</option>
          ))}
        </select>
        {nodeRoles.length === 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--color-warning-500)', marginTop: 4 }}>
            No <code>kubernetes.io/role</code> labels on this cluster — leave blank, or label a
            node first.
          </div>
        )}
        {selectedTainted && (
          <div style={{ fontSize: 11.5, color: 'var(--color-warning-500)', marginTop: 4 }}>
            ⚠ One or more nodes with this role are tainted — a pod may land on one and fail to
            schedule unless it tolerates that taint.
          </div>
        )}
      </>
    );
  } else if (spec.type === 'bool') {
    // A checkbox rather than a dropdown: it is a yes/no whose consequence lives in `help`.
    input = (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                      fontWeight: 400, cursor: 'pointer' }}>
        <input type="checkbox" checked={!!form[key]} onChange={(e) => set(e.target.checked)} />
        <span style={{ color: 'var(--muted)' }}>enable</span>
      </label>
    );
  } else if (spec.type === 'select') {
    // Two option shapes: plain primitives (e.g. members_per_shard: [1,3,5], pv_mode:
    // ["dynamic","manual"]) — rendered as both the value and the visible text — or
    // {value, label} objects (e.g. tls_mode) when the raw value isn't descriptive enough
    // to show a user directly. Rendering an OBJECT as `{o}` crashed with React error #31
    // ("Objects are not valid as a React child") before this branch existed — confirmed
    // live via a real browser error on the Elasticsearch catalog form's tls_mode field.
    const opts = (spec.options || []).map((o) =>
      (o !== null && typeof o === 'object') ? o : { value: o, label: String(o) }
    );
    const isNumeric = opts.every((o) => typeof o.value === 'number');
    input = (
      <select value={form[key] ?? spec.default}
              onChange={(e) => set(isNumeric ? Number(e.target.value) : e.target.value)}
              style={FIELD}>
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else if (key === 'storage_class') {
    // Every DB catalog form (Postgres, Mongo, Redis, Elasticsearch) shipped this as a plain
    // text input defaulting to "local-path" regardless of what the cluster actually has —
    // a DevOps user deploying to a cluster with differently-named classes (confirmed live:
    // jpr-dc-beta-13 has local-storage/obd-local-storage/obd42-storageclass, none named
    // local-path) had to already know and type the right name blind, no feedback if wrong.
    // Matches OpenShift's own pattern: storage class is always an explicit, informed
    // dropdown at deploy time, never a silent hardcoded assumption.
    //
    // Falls back to free text when storageClasses is empty (non-Rancher cluster — storage
    // health is Steve-only — or the fetch simply failed) so this never blocks a deploy the
    // old text field would have allowed.
    if (storageClasses.length > 0) {
      const unhealthy = (name) => storageClasses.find((sc) => sc.name === name)?.healthy === false;
      // spec.default ("local-path" everywhere) may not exist on THIS cluster at all
      // (e.g. jpr-dc-beta-13), or exist but be unhealthy — a <select> silently falls back
      // to its first <option> when its value matches none of them, which would commit that
      // choice to `form` without the user ever picking it. Only trust spec.default when
      // it's both present AND healthy; otherwise prefer any other healthy class.
      const defaultIsHealthy = storageClasses.find((sc) => sc.name === spec.default)?.healthy !== false
        && storageClasses.some((sc) => sc.name === spec.default);
      const effectiveDefault = defaultIsHealthy
        ? spec.default
        : (storageClasses.find((sc) => sc.healthy !== false)?.name || storageClasses[0].name);
      const selectedValue = form[key] ?? effectiveDefault;
      const selectedUnhealthy = selectedValue && unhealthy(selectedValue);
      input = (
        <>
          <select value={selectedValue} onChange={(e) => set(e.target.value)} style={FIELD}>
            {storageClasses.map((sc) => (
              <option key={sc.name} value={sc.name}>{sc.healthy === false ? `⚠ ${sc.name}` : sc.name}</option>
            ))}
          </select>
          {selectedUnhealthy && (
            <div style={{ fontSize: 11.5, color: 'var(--color-warning-500)', marginTop: 4 }}>
              ⚠ This storage class has no working provisioner on this cluster — a PVC using it
              will stay Pending. Check Storage Health on the cluster's detail page.
            </div>
          )}
        </>
      );
    } else {
      input = (
        <>
          <input type="text" value={form[key] ?? spec.default}
                 onChange={(e) => set(e.target.value)} style={FIELD} />
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>
            Could not load this cluster's storage classes — enter the name manually.
          </div>
        </>
      );
    }
  } else if (spec.type === 'node') {
    // Manual-PV node picker: real node names from the cluster, same validated-dropdown
    // principle as the role picker above — a typo'd node name would silently produce a
    // PersistentVolume pinned to a node that doesn't exist, and the pod would hang
    // Pending with no clearer a signal than the bug this whole feature exists to fix.
    const selectedTaints = taintsOf(form[key]);
    input = (
      <>
        <select value={form[key] ?? ''} onChange={(e) => set(e.target.value)} style={FIELD}>
          <option value="">(select a node)</option>
          {(clusterNodes || []).map((n) => (
            <option key={n} value={n}>{nodeTaints[n] ? `⚠ ${n}` : n}</option>
          ))}
        </select>
        {(clusterNodes || []).length === 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--color-warning-500)', marginTop: 4 }}>
            Could not load this cluster's node list.
          </div>
        )}
        {selectedTaints && (
          <div style={{ fontSize: 11.5, color: 'var(--color-warning-500)', marginTop: 4 }}>
            ⚠ Tainted: {selectedTaints.map((t) => `${t.key}=${t.value || ''}`).join(', ')} — a pod
            pinned here won't schedule unless it tolerates this.
          </div>
        )}
      </>
    );
  } else {
    input = (
      <input
        type={spec.type === 'int' ? 'number' : spec.type === 'password' ? 'password' : 'text'}
        value={form[key] ?? spec.default}
        min={spec.min} max={spec.max}
        placeholder={spec.type === 'password' ? 'blank = auto-generate' : ''}
        onChange={(e) => set(spec.type === 'int' ? Number(e.target.value) : e.target.value)}
        style={FIELD}
      />
    );
  }

  return (
    <div>
      <label style={{ display: 'block', fontWeight: 600, fontSize: 12.5, marginBottom: 5 }}>
        {spec.label}
      </label>
      {input}
      {/* Help sits under the input that causes the consequence, not in a doc. */}
      {spec.help && (
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
          {spec.help}
        </div>
      )}
    </div>
  );
}
